<template>
  <div class="actions-per-cause-btn">
    <div class="actions-per-cause-btn__button v-centerer" @click="displayActionsModal">
      <div>
        {{ $tc('message.N_ACTIONS', causeActions.length, { nb: causeActions.length }) }}
      </div>
      <Icon rotate-left>{{ arrowDownLine }}</Icon>
    </div>

    <!-- Show actions per cause -->
    <Modal
      ref="actionsModal"
      :value="isModalDisplayed"
      class="actions-per-cause-btn__actions-modal"
      @input="hideActionsModal"
    >
      <div>
        <h2 class="basic-sub-title actions-per-cause-btn__actions-modal__title">
          {{ `${$t('message.ACTIONS_PER_CAUSE_MODAL_TITLE')}${$t('message.TWO_DOTS')}${cause.text}` }}
        </h2>
        <PlanActionItem
          v-for="action in causeActions"
          :key="action.id"
          :action="action"
          :all-users="allTeams"
          :n-lines="currentTeam.planActionLines"
          :is-quick-action="action.isQuickAction === 1"
          hide-problem-data
          delete-btn-on-hover
          emit-click
          @click="displayAction(action)"
          @pdcaEdit="sendSocketUpdate"
          @leaderEdit="sendSocketUpdate"
          @deleteClick="deleteAction(action)"
        />
        <div class="h-v-centerer mt10em mb10em">
          <button
            class="generic-border generic-border--text-btn generic-border--blue-border"
            @click="addAction()"
          >
            <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
            <span>{{ $t('message.ADD_ACTION_PER_CAUSE') }}</span>
            <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
          </button>
        </div>
      </div>
    </Modal>

    <!-- Show a specific action -->
    <Modal
      ref="actionModal"
      :value="actionInModal !== null"
      class="actions-per-cause-btn__action-in-modal"
      xlarge
      @input="hideAction"
    >
      <div v-if="actionInModal">
        <ActionCreationModal :prop-id="actionInModal.id" in-popup @close="hideAction" />
      </div>
    </Modal>
  </div>
</template>

<script>
/**
 * Causes section of the problem sheet
 *
 * @vue-prop {Object} cause - Cause object
 * @vue-prop {Number} problemId - ID of the problem
 * @vue-prop {Boolean} [disabled] - Whether the problem sheet is editable
 */

import toastr from 'toastr';
import { isEqual } from 'lodash';
// Vuex
import { ADD_ACTION, EDIT_ACTION, DELETE_ACTION } from '@/store/modules/problem/action-types';
// Inport components
import ActionCreationModal from '@/components/edition-modals/action-creation-modal.vue';
import ActionItem from '@/components/action-item.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
import PlanActionItem from '@/components/plan-action-item.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { calert, preventPastingStyles, nestedFlatten } from '@/utils/main';
import { addExtraBold, arrowDownLine } from '@/assets/icons';

export default {

  name: 'ActionsPerCauseBtn',

  components: {
    ActionCreationModal,
    ActionItem,
    Icon,
    Modal,
    PlanActionItem,
  },

  mixins: [mixinSocketHandler],

  props: {
    problemId: {
      type: Number,
      required: true,
    },
    cause: {
      type: Object,
      required: true,
    },
    disabled: Boolean,
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      isModalDisplayed: false,
      actionInModal: null,
    };
  },

  computed: {
    /** Object: Problem as it was last set in the state */
    stateProblem() {
      return this.$store.state.problem.problems[`${this.problemId}`];
    },
    /** Array: Actions of the cause */
    causeActions() {
      return this.stateProblem.actions.filter(a => a.cause === this.cause.id);
    },
    /** Array: assignable teams for this problem */
    allTeams() {
      return this.$store.getters['problem/teamsForProblem'](this.problemId);
    },
  },

  watch: {
    isModalDisplayed(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.$nextTick(() => {
          this.documentBody.appendChild(this.$refs.actionsModal.$el);
        });
      }
    },
    actionInModal(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.$nextTick(() => {
          this.documentBody.appendChild(this.$refs.actionModal.$el);
        });
      }
    },
  },

  beforeDestroy() {
    if (this.$refs.actionsModal && this.$refs.actionsModal.$el) {
      this.$refs.actionsModal.$el.remove();
    }
    if (this.$refs.actionModal && this.$refs.actionModal.$el) {
      this.$refs.actionModal.$el.remove();
    }
  },

  methods: {
    /** Display the actions modal */
    displayActionsModal() {
      this.isModalDisplayed = true;
    },
    /** Hide the actions modal */
    hideActionsModal() {
      this.isModalDisplayed = false;
    },
    /** Display the modal for a specific action */
    displayAction(action) {
      this.actionInModal = action;
    },
    /** Hide the modal for a specific action */
    hideAction() {
      this.actionInModal = null;
    },
    /** Add an action to a cause or to the problem if no cause is selected */
    addAction() {
      const params = { id: this.cause.id, problemId: this.problemId };
      this.$store.dispatch(`problem/${ADD_ACTION}`, params).then((response) => {
        const { data } = response;
        if (data instanceof Object && data.id && data.problem > 0) {
          this.$nextTick(preventPastingStyles());
          this.sendSocketUpdate();
        } else if (data === -2) {
          calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
        } else {
          calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
        }
      });
    },
    /** Set an action to another cause */
    editAction(action, fields) {
      const params = { id: action.id, fromProblemSheet: 1 };
      if (!(fields instanceof Array)) {
        fields = [fields];
      }
      fields.forEach((field) => {
        if (action[field] !== undefined) {
          params[field] = action[field];
        }
      });
      this.$store.dispatch(`problem/${EDIT_ACTION}`, params).then((response) => {
        const { data } = response;
        if (data instanceof Object) {
          toastr.remove();
          toastr.success(this.$t('message.ACTION_UPDATED'));
          this.sendSocketUpdate();
          this.sendSocketMessage({
            team: data.affectTeam,
            id: data.id,
            msg: SocketMessages.CC_MSGS.ACTION_EDITION,
          });
          this.sendSocketMessage({
            team: data.affectTeam,
            msg: data.plan === 0 ? SocketMessages.CC_MSGS.PENDING : SocketMessages.CC_MSGS.ACTION_PLANS,
          });
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Delete the action from server */
    confirmDeleteAction(action) {
      const params = { id: action.id, problemId: this.problemId };
      this.$store.dispatch(`problem/${DELETE_ACTION}`, params).then((response) => {
        const { data } = response;
        if (data) {
          toastr.success(this.$t('message.ACTION_DELETED'));
          this.sendSocketMessage(Number(action.plan) === 0
            ? { msg: SocketMessages.CC_MSGS.PENDING_AND_PROBLEMS, id: this.problemId }
            : { msg: SocketMessages.CC_MSGS.PROBLEMS_AND_PLANS, id: this.problemId });
          this.sendSocketUpdate();
        } else if (Number(data) === -1) {
          toastr.error(this.$t('message.ACTION_DELETABLE_ASSIGNED'));
        } else {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
        }
      });
    },
    /** Ask the user to confirm action deletion */
    deleteAction(action, index) {
      calert(this.$t('message.CONFIRM_DELETE_ACTION'), [
        {
          title: this.$t('message.DELETE_ACTION'),
          action: () => { this.confirmDeleteAction(action, index); },
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage({
        msg: SocketMessages.CC_MSGS.PROBLEM_SHEET,
        id: this.this.problemId,
        socketCompany: 1,
      });
    },
    /** Display the modal that contains the action created for the 5why / Ishikawa */
    displayActionInModal(actionId) {
      this.$router.push(this.getActionCreationLink(actionId));
      this.sendSocketUpdate();
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.actions-per-cause-btn {
  &__button {
    justify-content: space-between;
    padding: 0.5rem;
    border-radius: $high-border-radius;
    cursor: pointer;

    .icon {
      width: 0.9rem;
      height: 0.9rem;
    }

    &:hover {
      background: $color-gray-background-hover;
    }
  }

  &__actions-modal {
    h2.basic-sub-title.actions-per-cause-btn__actions-modal__title {
      margin: 0;
      padding: 1rem 4rem 1rem 1rem;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;

      &:after {
        margin: 0 0 1rem -1rem;
        width: calc(100% + 1rem);
      }
    }

    .plan-action-item {
      margin: 0.7rem 2rem 0 2rem;
      width: calc(100% - 4rem);
    }
  }
}
</style>
